import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';

interface Section {
  id: string;
  code: string;
  name: string;
  department_id: string;
  department_name: string;
}
interface Department {
  id: string;
  code: string;
  name: string;
}

const EMPTY = { code: '', name: '', departmentId: '' };

export default function Sections() {
  const [sections, setSections] = useState<Section[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<Section | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    const [sec, dept] = await Promise.all([api.get('/masters/sections'), api.get('/masters/departments')]);
    setSections(sec.data.sections || []);
    setDepartments(dept.data.departments || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (row: Section) => {
    setEditingId(row.id);
    setForm({ code: row.code, name: row.name, departmentId: row.department_id });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await api.put(`/masters/sections/${editingId}`, form);
      } else {
        await api.post('/masters/sections', form);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/masters/sections/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete');
      setDeleteTarget(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Section</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'department_name', header: 'Department' },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={sections}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Section' : 'New Section'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select
            label="Department"
            required
            options={departments.map((d) => ({ value: d.id, label: d.name }))}
            value={form.departmentId}
            onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
          />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        message={`Delete section "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
